import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { useI18n } from '../context/I18nContext';
import { Phone, Filter, Loader, ArrowRight, Sparkles, RefreshCcw, Calendar } from 'lucide-react';
import { formatPhone, formatDate } from '../utils';
import { Skeleton } from '../components/Skeleton';

const NEXT_STATUS = {
  ny: 'kontaktet',
  kontaktet: 'avtalt',
  avtalt: 'lukket',
  lukket: null,
};

function ScoreBadge({ score }) {
  const s = Number(score || 0);
  const tone =
    s >= 70 ? 'bg-accent-green/15 text-accent-green border-accent-green/30'
      : s >= 40 ? 'bg-accent-yellow/15 text-accent-yellow border-accent-yellow/30'
        : 'bg-slate-500/15 text-slate-400 border-slate-500/30';
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${tone}`}>
      <Sparkles size={10} /> {s}
    </span>
  );
}

function LeadCard({ lead, onAdvance, onSetStatus, busy, columns, t }) {
  const next = NEXT_STATUS[lead.lead_status];
  const phone = lead.from_number || lead.phone_number || '';
  const summary = (lead.call_summary || lead.service_requested || lead.call_intent || '').trim();
  const nextLabel = next ? (columns.find(c => c.key === next)?.label || next) : '';
  return (
    <div
      data-testid={`lead-card-${lead.call_id}`}
      className="bg-navy-800 border border-navy-600 rounded-lg p-3 space-y-2 hover:border-accent-blue/50 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-white truncate">{lead.caller_name || t('leadboard_unknown_caller', 'Ukjent innringer')}</div>
          <div className="text-xs text-slate-500 font-mono truncate">{phone ? formatPhone(phone) : '—'}</div>
        </div>
        <ScoreBadge score={lead.lead_score} />
      </div>

      {summary && (
        <p className="text-xs text-slate-400 leading-relaxed line-clamp-3">{summary}</p>
      )}

      <div className="flex items-center justify-between text-[10px] text-slate-600 pt-1 border-t border-navy-700">
        <span className="flex items-center gap-1"><Calendar size={10} /> {formatDate(lead.created_at)}</span>
        {lead.call_intent && <span className="px-1.5 py-0.5 rounded bg-navy-700 text-slate-400">{lead.call_intent}</span>}
      </div>

      <div className="flex items-center gap-2 pt-1">
        {phone && (
          <a
            href={`tel:${phone}`}
            data-testid={`lead-call-${lead.call_id}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 bg-navy-700 border border-navy-600 text-xs text-slate-300 rounded-md hover:border-accent-green hover:text-accent-green transition-colors"
          >
            <Phone size={12} /> {t('leadboard_call_back', 'Ring tilbake')}
          </a>
        )}
        {next && (
          <button
            onClick={() => onAdvance(lead, next)}
            disabled={busy}
            data-testid={`lead-advance-${lead.call_id}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 bg-accent-blue/15 border border-accent-blue/30 text-xs text-accent-blue rounded-md hover:bg-accent-blue/25 transition-colors disabled:opacity-50"
            title={`${t('leadboard_move_to', 'Flytt til')} ${nextLabel}`}
          >
            {busy ? <Loader size={12} className="animate-spin" /> : <ArrowRight size={12} />} {t('leadboard_next_step', 'Neste steg')}
          </button>
        )}
      </div>

      <div className="flex items-center justify-end gap-1 pt-1">
        {columns.filter(c => c.key !== lead.lead_status).map(c => (
          <button
            key={c.key}
            onClick={() => onSetStatus(lead, c.key)}
            disabled={busy}
            className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-navy-900 border border-navy-700 text-slate-500 hover:text-white hover:border-slate-500 transition-colors disabled:opacity-50"
            title={`${t('leadboard_set_status_to', 'Sett status til')} ${c.label}`}
          >
            {c.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function LeadBoard() {
  const toast = useToast();
  const { t } = useI18n();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [hotOnly, setHotOnly] = useState(false);
  const [days, setDays] = useState(60);
  const [refreshing, setRefreshing] = useState(false);

  const COLUMNS = useMemo(() => ([
    { key: 'ny', label: t('leadboard_col_ny', 'Ny'), accent: 'border-accent-blue/40 text-accent-blue' },
    { key: 'kontaktet', label: t('leadboard_col_kontaktet', 'Kontaktet'), accent: 'border-accent-yellow/40 text-accent-yellow' },
    { key: 'avtalt', label: t('leadboard_col_avtalt', 'Avtalt'), accent: 'border-accent-green/40 text-accent-green' },
    { key: 'lukket', label: t('leadboard_col_lukket', 'Lukket'), accent: 'border-slate-500/40 text-slate-400' },
  ]), [t]);

  const load = async (opts = {}) => {
    const showSpinner = opts.silent !== true;
    if (showSpinner && leads.length === 0) setLoading(true);
    setRefreshing(true);
    try {
      const minScore = hotOnly ? 7 : 5;
      const { data } = await api.get(`/api/leads/board?min_score=${minScore}&days=${days}`);
      setLeads(Array.isArray(data?.leads) ? data.leads : []);
    } catch (err) {
      if (err.response?.status === 404) {
        setLeads([]);
      } else {
        toast.error(t('leadboard_load_error', 'Kunne ikke laste leads'));
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [hotOnly, days]);

  const grouped = useMemo(() => {
    const out = { ny: [], kontaktet: [], avtalt: [], lukket: [] };
    for (const l of leads) {
      const k = (l.lead_status || 'ny').toLowerCase();
      if (out[k]) out[k].push(l); else out.ny.push(l);
    }
    return out;
  }, [leads]);

  const setStatus = async (lead, status) => {
    if (!lead?.call_id) return;
    setBusy(b => ({ ...b, [lead.call_id]: true }));
    // Optimistic update
    setLeads(prev => prev.map(l => l.call_id === lead.call_id ? { ...l, lead_status: status } : l));
    try {
      await api.patch(`/api/leads/${encodeURIComponent(lead.call_id)}/status`, { status });
      const colLabel = COLUMNS.find(c => c.key === status)?.label || status;
      toast.success(`${t('leadboard_moved_to', 'Lead flyttet til')} "${colLabel}"`);
    } catch (err) {
      toast.error(t('leadboard_update_error', 'Kunne ikke oppdatere status'));
      // Reload to resync
      load({ silent: true });
    } finally {
      setBusy(b => ({ ...b, [lead.call_id]: false }));
    }
  };

  const advance = (lead, next) => setStatus(lead, next);

  return (
    <div data-testid="lead-board-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">{t('leadboard_title', 'Leads')}</h1>
          <p className="text-slate-500 text-sm mt-1">{t('leadboard_subtitle', 'Følg opp kvalifiserte innringere — flytt mellom steg etter hvert som du jobber dem.')}</p>
        </div>
        <button
          onClick={() => load()}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-sm text-slate-300 hover:border-accent-blue hover:text-white transition-colors disabled:opacity-50"
          data-testid="lead-board-refresh"
        >
          {refreshing ? <Loader size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
          {t('leadboard_refresh', 'Oppdater')}
        </button>
      </div>

      {/* Filter bar */}
      <div className="mb-5 flex flex-wrap items-center gap-3 bg-navy-800 border border-navy-600 rounded-lg p-3">
        <Filter size={14} className="text-slate-500" />
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={hotOnly}
            onChange={(e) => setHotOnly(e.target.checked)}
            data-testid="lead-board-filter-hot"
            className="accent-accent-blue"
          />
          {t('leadboard_filter_hot_only', 'Kun hot leads (score ≥ 7)')}
        </label>
        <div className="h-4 w-px bg-navy-600" />
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <span className="text-xs text-slate-500">{t('leadboard_period', 'Periode:')}</span>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="lead-board-filter-days"
            className="bg-navy-900 border border-navy-600 text-sm text-white rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-accent-blue"
          >
            <option value={7}>{t('leadboard_last_7', 'Siste 7 dager')}</option>
            <option value={14}>{t('leadboard_last_14', 'Siste 14 dager')}</option>
            <option value={30}>{t('leadboard_last_30', 'Siste 30 dager')}</option>
            <option value={60}>{t('leadboard_last_60', 'Siste 60 dager')}</option>
            <option value={90}>{t('leadboard_last_90', 'Siste 90 dager')}</option>
          </select>
        </label>
        <span className="ml-auto text-xs text-slate-500">{leads.length} {t('leadboard_total_suffix', 'leads totalt')}</span>
      </div>

      {/* Board — horizontal scroll on mobile, grid on md+ */}
      <div
        className="lead-board-scroll md:overflow-visible overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 pb-2"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div className="flex gap-4 md:grid md:grid-cols-2 xl:grid-cols-4">
          {COLUMNS.map(col => {
            const items = grouped[col.key] || [];
            return (
              <div
                key={col.key}
                data-testid={`lead-column-${col.key}`}
                className="bg-navy-900/50 border border-navy-700 rounded-lg flex flex-col min-h-[400px] flex-shrink-0 w-[280px] md:w-auto md:min-w-0"
              >
                <div className={`px-4 py-3 border-b border-navy-700 flex items-center justify-between rounded-t-lg ${col.accent}`}>
                  <span className="text-xs font-semibold tracking-widest uppercase">{col.label}</span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-navy-800 text-slate-400 border border-navy-700">{items.length}</span>
                </div>
                <div className="p-3 space-y-2 flex-1 overflow-y-auto max-h-[70vh]">
                  {loading ? (
                    <>
                      <Skeleton height={96} />
                      <Skeleton height={96} />
                    </>
                  ) : items.length === 0 ? (
                    <div className="text-center text-xs text-slate-600 py-8">
                      {t('leadboard_empty_column', 'Ingen leads i denne kolonnen ennå.')}
                    </div>
                  ) : (
                    items.map(lead => (
                      <LeadCard
                        key={lead.call_id}
                        lead={lead}
                        busy={!!busy[lead.call_id]}
                        onAdvance={advance}
                        onSetStatus={setStatus}
                        columns={COLUMNS}
                        t={t}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="md:hidden text-[10px] text-slate-600 text-center mt-1">
        {t('leadboard_scroll_hint', '← sveip for flere kolonner →')}
      </div>
    </div>
  );
}
